import { useState } from "react";
import type {
  Tag,
  UpdateTagRequest,
  CreateTagRequest,
} from "@/services/api/tags";
import { useTagsPanel } from "./tags/useTagsPanel";
import { TagsPanelHeader } from "./tags/TagsPanelHeader";
import { TagsTable } from "./tags/TagsTable";
import { TagEditDialog } from "./tags/TagEditDialog";
import { TagDeleteDialog } from "./tags/TagDeleteDialog";
import { TagCreateDialog } from "./tags/TagCreateDialog";

export function TagsPanel() {
  const {
    tags,
    isLoading,
    sortField,
    sortDirection,
    editingTag,
    deletingTag,
    handleSort,
    handleCreate,
    handleUpdate,
    handleDelete,
    setEditingTag,
    setDeletingTag,
    createIsLoading,
    updateIsLoading,
    deleteIsLoading,
    tagsCount,
  } = useTagsPanel();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const handleColorChange = (tag: Tag, color: string) => {
    if (tag.type === "AiGenerated") {
      return;
    }

    const payload: UpdateTagRequest = {
      name: tag.name ?? null,
      color,
      isActive: tag.isActive,
    };

    handleUpdate(tag.id, payload);
  };

  return (
    <div className="w-full space-y-6">
      <TagsPanelHeader
        tagsCount={tagsCount}
        onCreateClick={() => setIsCreateDialogOpen(true)}
      />

      <div className="rounded-xl border border-grid-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-ink-500">
            Loading tags...
          </div>
        ) : (
          <TagsTable
            tags={tags}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onEdit={(tag: Tag) => {
              if (tag.type !== "AiGenerated") {
                setEditingTag(tag);
              }
            }}
            onDelete={(tag: Tag) => {
              if (tag.type !== "AiGenerated") {
                setDeletingTag(tag);
              }
            }}
            onColorChange={handleColorChange}
          />
        )}
      </div>

      <TagCreateDialog
        open={isCreateDialogOpen}
        isLoading={createIsLoading}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={(data: CreateTagRequest) => {
          handleCreate(data);
          setIsCreateDialogOpen(false);
        }}
      />

      <TagEditDialog
        open={!!editingTag}
        tag={editingTag}
        isLoading={updateIsLoading}
        onOpenChange={(open) => !open && setEditingTag(null)}
        onSubmit={handleUpdate}
      />

      <TagDeleteDialog
        open={!!deletingTag}
        tag={deletingTag}
        isLoading={deleteIsLoading}
        onOpenChange={(open) => !open && setDeletingTag(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
